import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Pages
import CoachLandingPage from "./pages/CoachLandingPage";
import LandingPage from "./pages/LandingPage";
import ExplorePage from "./pages/ExplorePage";
import SessionDetailPage from "./pages/SessionDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import UserDashboard from "./pages/UserDashboard";
import CoachDashboard from "./pages/CoachDashboard";
import CreateSessionPage from "./pages/CreateSessionPage";
import EditSessionPage from "./pages/EditSessionPage";
import CoachCalendarPage from "./pages/CoachCalendarPage";
import CoachProfilePage from "./pages/CoachProfilePage";
import UserProfilePage from "./pages/UserProfilePage";
import FavoritesPage from "./pages/FavoritesPage";
import BecomeCoachPage from "./pages/BecomeCoachPage";
import AdminCoachApplicationsPage from "./pages/AdminCoachApplicationsPage";
import AdminCoachLeadsPage from "./pages/AdminCoachLeadsPage";
import AdminDatabasePage from "./pages/AdminDatabasePage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import OutfitPage from "./pages/OutfitPage";
import ComingSoonPage, { isPrelaunchAuthenticated } from "./pages/ComingSoonPage";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { LanguageProvider } from "./i18n";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const verify = async () => {
      if (!user) {
        await checkAuth();
      }
      setIsChecking(false);
    };
    verify();
  }, [user, checkAuth]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// B2C Protected Route - Shows Coming Soon if not pre-launch authenticated
const ProtectedB2CRoute = ({ children }) => {
  const [isAuth, setIsAuth] = useState(isPrelaunchAuthenticated());
  
  if (!isAuth) {
    return <ComingSoonPage onAuthenticated={() => setIsAuth(true)} />;
  }
  
  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* PUBLIC ROUTES - No protection needed */}
      {/* Coach Landing Page - New Home */}
      <Route path="/" element={<CoachLandingPage />} />
      
      {/* Become Coach - Public for coach recruitment */}
      <Route path="/become-coach" element={<BecomeCoachPage />} />
      
      {/* Contact & Legal - Always public */}
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* B2C ROUTES - These require Coming Soon authentication */}
      <Route path="/explore" element={<ProtectedB2CRoute><ExplorePage /></ProtectedB2CRoute>} />
      <Route path="/app" element={<ProtectedB2CRoute><LandingPage /></ProtectedB2CRoute>} />
      <Route path="/session/:sessionId" element={<ProtectedB2CRoute><SessionDetailPage /></ProtectedB2CRoute>} />
      <Route path="/login" element={<ProtectedB2CRoute><LoginPage /></ProtectedB2CRoute>} />
      <Route path="/register" element={<ProtectedB2CRoute><RegisterPage /></ProtectedB2CRoute>} />
      <Route path="/outfit" element={<ProtectedB2CRoute><OutfitPage /></ProtectedB2CRoute>} />
      
      {/* User Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/favorites" 
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Coach Protected Routes */}
      <Route 
        path="/coach/dashboard" 
        element={
          <ProtectedRoute requiredRole="coach">
            <CoachDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/coach/sessions/new" 
        element={
          <ProtectedRoute requiredRole="coach">
            <CreateSessionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/coach/sessions/:sessionId/edit" 
        element={
          <ProtectedRoute requiredRole="coach">
            <EditSessionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/coach/calendar" 
        element={
          <ProtectedRoute requiredRole="coach">
            <CoachCalendarPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/coach/profile" 
        element={
          <ProtectedRoute requiredRole="coach">
            <CoachProfilePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin/coach-applications" 
        element={
          <ProtectedRoute>
            <AdminCoachApplicationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/coach-leads" 
        element={
          <ProtectedRoute>
            <AdminCoachLeadsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/database" 
        element={
          <ProtectedRoute>
            <AdminDatabasePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // No longer blocking entire app - protection is handled per-route
  return (
    <LanguageProvider>
      <AuthProvider>
        <FavoritesProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-background">
              <AppRouter />
              <Toaster position="top-right" richColors />
            </div>
          </BrowserRouter>
        </FavoritesProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
